import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Alert,
  Stack,
  useTheme,
} from '@mui/material';
import {
  FitnessCenter as FitnessCenterIcon,
  AccessTime as TimeIcon,
  LocalFireDepartment as CaloriesIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import api from '../utils/api';

const WorkoutLogList = ({ onEdit, refreshTrigger }) => {
  const theme = useTheme();
  
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedWorkout, setSelectedWorkout] = useState(null);

  useEffect(() => {
    fetchWorkouts();
  }, [refreshTrigger]);

  const fetchWorkouts = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/workout-logs');
      setWorkouts(response.data.data || []);
    } catch (error) {
      setError('Failed to load workouts');
      console.error('Error fetching workouts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, workout) => {
    setAnchorEl(event.currentTarget);
    setSelectedWorkout(workout);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedWorkout(null);
  };

  const handleEdit = () => {
    if (selectedWorkout && onEdit) {
      onEdit(selectedWorkout);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!selectedWorkout) return;

    try {
      await api.delete(`/workout-logs/${selectedWorkout._id}`);
      setWorkouts(prev => prev.filter(w => w._id !== selectedWorkout._id));
      handleMenuClose();
    } catch (error) {
      console.error('Error deleting workout:', error);
      setError('Failed to delete workout');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getIntensityColor = (intensity) => {
    switch (intensity) {
      case 'low': return 'success';
      case 'moderate': return 'warning';
      case 'high': return 'error';
      case 'very_high': return 'error';
      default: return 'default';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'strength': return '💪';
      case 'cardio': return '🏃';
      case 'mixed': return '🔄';
      case 'sports': return '⚽';
      case 'flexibility': return '🧘';
      default: return '🏋️';
    }
  };

  const getFeelingEmoji = (feeling) => {
    switch (feeling) {
      case 'terrible': return '😰';
      case 'bad': return '😔';
      case 'okay': return '😐';
      case 'good': return '😊';
      case 'great': return '😍';
      default: return '😊';
    }
  };

  if (loading) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={i}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Skeleton width="60%" height={32} />
                  <Skeleton variant="circular" width={24} height={24} />
                </Box>
                <Box display="flex" gap={1} mb={2}>
                  <Skeleton width={60} height={24} />
                  <Skeleton width={80} height={24} />
                </Box>
                <Skeleton width="100%" height={20} sx={{ mb: 1 }} />
                <Skeleton width="80%" height={20} sx={{ mb: 1 }} />
                <Skeleton width="90%" height={20} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  if (workouts.length === 0) {
    return (
      <Card sx={{ textAlign: 'center', py: 6 }}>
        <CardContent>
          <FitnessCenterIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No workout logs yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start logging your workouts to track your fitness journey!
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        {workouts.map((workout) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={workout._id}>
            <Card 
              sx={{ 
                height: '100%',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[8]
                }
              }}
            >
              <CardContent>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {getTypeIcon(workout.type)} {workout.title}
                    </Typography>
                  </Box>
                  <IconButton 
                    size="small"
                    onClick={(e) => handleMenuOpen(e, workout)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>

                {/* Date and Time */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {formatDate(workout.date)} at {formatTime(workout.date)}
                </Typography>

                {/* Type and Intensity Chips */}
                <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                  <Chip 
                    label={workout.type}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ textTransform: 'capitalize' }}
                  />
                  <Chip 
                    label={workout.intensity}
                    size="small"
                    color={getIntensityColor(workout.intensity)}
                    variant="outlined"
                    sx={{ textTransform: 'capitalize' }}
                  />
                  {workout.feeling && (
                    <Chip 
                      label={`${getFeelingEmoji(workout.feeling)} ${workout.feeling}`}
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  )}
                </Box>

                {/* Stats */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={4}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {workout.duration}m
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={4}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CaloriesIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {workout.caloriesBurned || 'N/A'} cal
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={4}>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <FitnessCenterIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {workout.exercises?.length || 0} ex
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Facility and Trainer */}
                {(workout.facility || workout.trainer) && (
                  <Box sx={{ mb: 2 }}>
                    {workout.facility && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        📍 {workout.facility.name}
                      </Typography>
                    )}
                    {workout.trainer && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        👨‍💼 {workout.trainer.firstName} {workout.trainer.lastName}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Exercise Summary */}
                {workout.exercises && workout.exercises.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Exercises:
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {workout.exercises.slice(0, 3).map((exercise, index) => (
                        <Chip
                          key={index}
                          label={exercise.name}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.7rem',
                            height: 20,
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      ))}
                      {workout.exercises.length > 3 && (
                        <Chip
                          label={`+${workout.exercises.length - 3} more`}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.7rem',
                            height: 20,
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Tags */}
                {workout.tags && workout.tags.length > 0 && (
                  <Box>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {workout.tags.map((tag, index) => (
                        <Chip
                          key={index}
                          label={`#${tag}`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                          sx={{ 
                            fontSize: '0.7rem',
                            height: 20,
                            '& .MuiChip-label': { px: 1 }
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Notes Preview */}
                {workout.notes && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      "{workout.notes}"
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

export default WorkoutLogList;